import {
  EMPTY_DOC,
  richDocText,
  teamCommonRulesSchema,
  teamRulesSchema,
  teamRulesVersionSchema,
  type AdminTeam,
  type RichDoc,
  type TeamCommonRules,
  type TeamRules,
  type TeamRulesVersion,
  type TeamRulesVersionSummary,
} from '@growup/site-shared';
import { Button, Card, Disclosure, Notice, StatusBadge } from '@growup/ui';
import { useEffect, useState } from 'react';

import { getJson, messageOf, send } from '../api';
import { RichEditor } from '../content/RichEditor';
import { formatDateTime } from '../format';

const BASE = '/api/site/teams';

const dayFormat = new Intl.DateTimeFormat('pt-PT', {
  timeZone: 'Europe/Lisbon',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});
const dayOf = (iso: string) => dayFormat.format(new Date(iso));

/**
 * O corpo comum do regulamento (parte 6, docs/equipas, RF-EQ14), so do Gestor
 * de Equipas. Cada publicacao e uma versao nova que todos tem de aceitar.
 */
export function CommonRulesView() {
  const [rules, setRules] = useState<TeamCommonRules | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getJson(`${BASE}/rules/common`, teamCommonRulesSchema)
      .then(setRules)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);

  if (rules === null) {
    return error ? <Notice tone="danger">{error}</Notice> : <p>A carregar…</p>;
  }
  return (
    <Card headingLevel={2} title="Regulamento comum">
      <RulesEditor
        intro="Vale para todas as equipas. Cada equipa pode juntar a sua parte, na ficha da equipa."
        label="Corpo comum do regulamento"
        current={rules.current}
        history={rules.history}
        onPublish={async (body) =>
          setRules(teamCommonRulesSchema.parse(await send('PUT', `${BASE}/rules/common`, { body })))
        }
      />
    </Card>
  );
}

/** A parte da equipa e quem do lineup atual ja aceitou o regulamento. */
export function TeamRulesCard({ team }: { team: AdminTeam }) {
  const [rules, setRules] = useState<TeamRules | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getJson(`${BASE}/${team.id}/rules`, teamRulesSchema)
      .then(setRules)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [team.id]);

  if (rules === null) {
    return (
      <Card headingLevel={2} title="Regulamento">
        {error ? <Notice tone="danger">{error}</Notice> : <p>A carregar…</p>}
      </Card>
    );
  }
  const pending = rules.members.filter((m) => m.status === 'pending').length;

  return (
    <Card headingLevel={2} title="Regulamento">
      <div className="flex flex-col gap-5">
        {rules.common ? (
          <Disclosure
            summary={`Corpo comum, versão ${String(rules.common.version)} (de ${dayOf(rules.common.publishedAt)})`}
          >
            <PlainText doc={rules.common.body} />
          </Disclosure>
        ) : (
          <p className="text-gu-ink-muted">
            Ainda não há corpo comum (escreve-o o Gestor de Equipas, em Regulamento comum).
          </p>
        )}
        {team.status === 'archived' ? (
          rules.team ? (
            <Disclosure summary={`Parte da equipa, versão ${String(rules.team.version)}`}>
              <PlainText doc={rules.team.body} />
            </Disclosure>
          ) : null
        ) : (
          <RulesEditor
            label="Parte desta equipa"
            current={rules.team}
            history={rules.history}
            onPublish={async (body) =>
              setRules(
                teamRulesSchema.parse(await send('PUT', `${BASE}/${team.id}/rules`, { body })),
              )
            }
          />
        )}
        {rules.members.length > 0 && (rules.common || rules.team) ? (
          <div className="flex flex-col gap-2">
            <h3 className="font-sub text-label font-bold uppercase text-gu-ink-muted">
              Aceitação no lineup atual
              {pending > 0 ? ` · ${String(pending)} por aceitar` : ' · todos aceitaram'}
            </h3>
            <ul className="flex flex-col divide-y divide-gu-line">
              {rules.members.map((m) => (
                <li key={m.accountId} className="flex flex-wrap items-center gap-2 py-2">
                  <span className="font-semibold">{m.login}</span>
                  {m.status === 'accepted' ? (
                    <StatusBadge tone="success">Aceite</StatusBadge>
                  ) : (
                    <StatusBadge tone="warning">Por aceitar</StatusBadge>
                  )}
                  <span className="text-caption text-gu-ink-muted">
                    {m.acceptedAt
                      ? `${m.status === 'accepted' ? 'aceite' : 'última aceitação'} a ${dayOf(m.acceptedAt)}${m.byGuardian ? ' pelo encarregado' : ''}`
                      : m.youth
                        ? 'aceita o encarregado'
                        : 'aceita na sua conta do site'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function RulesEditor({
  intro,
  label,
  current,
  history,
  onPublish,
}: {
  intro?: string;
  label: string;
  current: TeamRulesVersion | null;
  history: TeamRulesVersionSummary[];
  onPublish: (body: RichDoc) => Promise<void>;
}) {
  const [body, setBody] = useState<RichDoc>(current?.body ?? EMPTY_DOC);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  // Muda a chave para o editor recomecar com o texto publicado.
  const [round, setRound] = useState(0);

  const publish = async () => {
    if (
      current &&
      !window.confirm('Publicar uma versão nova? Todos os jogadores terão de a aceitar outra vez.')
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      await onPublish(body);
      setDone('Versão nova publicada. Quem está nas equipas tem de a aceitar.');
      setRound((r) => r + 1);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {intro ? <p className="text-gu-ink-muted">{intro}</p> : null}
      <p className="text-caption text-gu-ink-muted">
        {current
          ? `Em vigor: versão ${String(current.version)}, publicada a ${formatDateTime(current.publishedAt)} por ${current.publishedBy}.`
          : 'Ainda não publicado.'}
      </p>
      <RichEditor
        key={`${current?.id ?? 'novo'}-${String(round)}`}
        label={label}
        value={body}
        onChange={setBody}
        error={error ?? undefined}
        hint="Ao publicar fica uma versão nova; as anteriores ficam guardadas."
      />
      {done ? <Notice tone="success">{done}</Notice> : null}
      <div>
        <Button size="sm" disabled={busy} onClick={() => void publish()}>
          {current ? 'Publicar versão nova' : 'Publicar'}
        </Button>
      </div>
      {history.length > 1 ? <History history={history.slice(1)} /> : null}
    </div>
  );
}

function History({ history }: { history: TeamRulesVersionSummary[] }) {
  const [open, setOpen] = useState<TeamRulesVersion | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="flex flex-col gap-2">
      <h3 className="font-sub text-label font-bold uppercase text-gu-ink-muted">
        Versões anteriores
      </h3>
      <ul className="flex flex-col gap-1 text-gu-ink-muted">
        {history.map((h) => (
          <li key={h.id} className="flex flex-wrap items-center gap-2">
            Versão {h.version} · {dayOf(h.publishedAt)} · {h.publishedBy}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                if (open?.id === h.id) return setOpen(null);
                getJson(`${BASE}/rules/versions/${h.id}`, teamRulesVersionSchema)
                  .then(setOpen)
                  .catch((e: unknown) => setError(messageOf(e)));
              }}
            >
              {open?.id === h.id ? 'Fechar' : 'Ler'}
            </Button>
          </li>
        ))}
      </ul>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {open ? (
        <div className="border-2 border-gu-line p-3">
          <PlainText doc={open.body} />
        </div>
      ) : null}
    </div>
  );
}

function PlainText({ doc }: { doc: RichDoc }) {
  return <p className="whitespace-pre-line text-gu-ink-muted">{richDocText(doc)}</p>;
}
